import { motion } from 'framer-motion'
import HanDuShell from './HanDuShell'
import { fadeUp, stagger, viewportOnce } from '../../motion'
import { legal } from '../data'

const SECTIONS = [
  ['summary', 'The short version'],
  ['controller', 'Who is responsible'],
  ['collect', 'What HanDu collects'],
  ['not-collected', 'What HanDu does not collect'],
  ['why', 'Why each item is collected'],
  ['processors', 'Who else handles your data'],
  ['where', 'Where your data is stored'],
  ['retention', 'How long it is kept'],
  ['deletion', 'Deleting your account'],
  ['rights', 'Your rights'],
  ['security', 'How it is protected'],
  ['children', "Children's privacy"],
  ['labels', 'App Store privacy labels'],
  ['changes', 'Changes to this policy'],
  ['contact', 'Contact'],
]

export default function Privacy() {
  return (
    <HanDuShell>
      <article className="mx-auto max-w-3xl px-5 py-14 sm:px-8 sm:py-20">
        <motion.div variants={stagger} initial="hidden" animate="show">
          <motion.p variants={fadeUp} className="handu-eyebrow mb-3">
            HanDu 汉读
          </motion.p>
          <motion.h1
            variants={fadeUp}
            className="text-4xl font-bold tracking-tight sm:text-5xl"
          >
            Privacy Policy
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-4 text-handu-soft">
            Last updated {legal.privacyEffective}. This policy covers the HanDu
            iOS app and this website.
          </motion.p>

          <motion.nav
            variants={fadeUp}
            aria-label="Contents"
            className="handu-panel mt-10 p-6"
          >
            <h2 className="handu-eyebrow mb-3">Contents</h2>
            <ol className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
              {SECTIONS.map(([id, title], i) => (
                <li key={id} className="flex gap-2">
                  <span className="text-handu-soft/60">{i + 1}.</span>
                  <a
                    href={`#${id}`}
                    className="text-handu-primary transition-opacity hover:opacity-70"
                  >
                    {title}
                  </a>
                </li>
              ))}
            </ol>
          </motion.nav>
        </motion.div>

        <Sec id="summary" n={1} title="The short version">
          <P>
            HanDu asks for the least it can. To use the app you need an account,
            because your reading progress is stored against it and follows you
            between devices. That means HanDu holds your email address and a
            record of which texts you have finished — and, beyond the technical
            records any login system keeps, essentially nothing else.
          </P>
          <P>
            There is no analytics SDK in the app, no crash reporter, no
            advertising identifier, and no third-party tracker. Nothing about
            your data is sold or shared for advertising. You can delete your
            account, and everything attached to it, from inside the app at any
            time.
          </P>
        </Sec>

        <Sec id="controller" n={2} title="Who is responsible">
          <P>
            HanDu is an independent app made and operated by Yusuf Brigui, who
            is the data controller for the personal data described here. For any
            question about this policy or about your data, write to{' '}
            <Mail /> and you will be dealing with the developer directly.
          </P>
        </Sec>

        <Sec id="collect" n={3} title="What HanDu collects">
          <H3>Account information</H3>
          <P>
            If you sign up with an email address and password, HanDu stores your
            email address. Your password is sent over an encrypted connection to
            the authentication service, which stores only a salted hash of it —
            the plain password is never stored and is not visible to the
            developer at any point.
          </P>
          <P>
            Creating an account triggers a confirmation email, and asking to
            reset your password triggers a reset email. These are sent by the
            authentication provider on HanDu&apos;s behalf and are the only
            emails the app sends you. There is no newsletter and no marketing
            email.
          </P>

          <H3>Sign in with Apple</H3>
          <P>
            If you choose Sign in with Apple instead, the app receives an
            identity token from Apple and exchanges it for a HanDu session. The
            app never sees your Apple ID password. If you choose Apple&apos;s
            Hide My Email option, the only address HanDu ever holds is the
            private relay address Apple generates for you — the developer cannot
            see your real one.
          </P>

          <H3>Reading progress</H3>
          <P>
            When you mark a text as read, HanDu stores one row containing your
            user identifier, the identifier of the text, and the time you marked
            it. That is the entire record. HanDu does not store which words you
            tapped, how long you spent on a page, how far down a text you
            scrolled, or how often you looked something up. None of that leaves
            your device, because none of it is collected in the first place.
          </P>
          <P>
            Unmarking a text deletes that row outright rather than flagging it,
            so an unmarked text leaves no trace behind.
          </P>

          <H3>Technical and security records</H3>
          <P>
            The authentication service keeps the operational records any login
            system needs in order to work and to stay secure: account creation
            and last sign-in timestamps, the IP address a request came from, and
            session tokens. These are used to keep your account working and to
            detect abuse, not to build a profile of you.
          </P>

          <H3>Settings stored only on your device</H3>
          <P>
            Your reading preferences — whether pinyin is shown, and whether a tap
            translates a word or a sentence — are stored locally on your iPhone
            and never transmitted anywhere. Your session token is held in the iOS
            Keychain. Deleting the app removes both.
          </P>
        </Sec>

        <Sec id="not-collected" n={4} title="What HanDu does not collect">
          <P>
            This list is exhaustive as of the date above, and it describes the
            app as built rather than an intention:
          </P>
          <UL
            items={[
              'No analytics or telemetry SDK of any kind, and no crash-reporting service.',
              'No advertising identifiers (IDFA), no ad networks, and no ads.',
              'No third-party trackers, pixels, or social media SDKs.',
              'No location data, precise or coarse.',
              'No access to your contacts, photos, camera, microphone, calendar, or health data — the app never asks for these permissions.',
              'No payment or financial information; HanDu has no purchases.',
              'No content you write, because the app has no field to write into: the library is read-only material provided by HanDu.',
              'No cross-app or cross-site tracking, in the sense Apple’s App Tracking Transparency uses the term. HanDu does not present a tracking prompt because it does not track.',
            ]}
          />
          <P>
            This website is equally plain: it serves static pages, sets no
            cookies, and runs no analytics, which is why you were not asked to
            dismiss a consent banner.
          </P>
        </Sec>

        <Sec id="why" n={5} title="Why each item is collected">
          <P>
            For users in the European Economic Area and the United Kingdom, the
            legal bases under the GDPR are as follows.
          </P>
          <Table
            head={['Data', 'Purpose', 'Legal basis']}
            rows={[
              [
                'Email address and password hash',
                'Creating your account and signing you in',
                'Performance of a contract (Art. 6(1)(b))',
              ],
              [
                'Reading progress',
                'Showing what you have finished and syncing it across your devices',
                'Performance of a contract (Art. 6(1)(b))',
              ],
              [
                'Sign-in timestamps, IP address, session tokens',
                'Keeping accounts secure and preventing abuse',
                'Legitimate interests (Art. 6(1)(f))',
              ],
              [
                'Confirmation and password-reset emails',
                'Verifying your address and letting you recover access',
                'Performance of a contract (Art. 6(1)(b))',
              ],
            ]}
          />
        </Sec>

        <Sec id="processors" n={6} title="Who else handles your data">
          <P>
            HanDu uses two service providers, and no others. Neither is
            permitted to use your data for its own purposes.
          </P>
          <Table
            head={['Provider', 'What it does', 'What it receives']}
            rows={[
              [
                'Supabase',
                'Authentication, database, and the account-deletion function',
                'Your email address, password hash, reading progress, and sign-in records',
              ],
              [
                'Apple',
                'Sign in with Apple, and App Store distribution',
                'Only what Apple already holds as your account provider; HanDu receives an identity token in return',
              ],
            ]}
          />
          <P>
            Your data is not sold, rented, or shared with advertisers, data
            brokers, or anyone else. It would only ever be disclosed further if a
            valid legal obligation required it.
          </P>
        </Sec>

        <Sec id="where" n={7} title="Where your data is stored">
          <P>
            HanDu&apos;s database and authentication service are hosted in the
            European Union — specifically in Ireland (AWS <Code>eu-west-1</Code>
            ). For users in the EEA and the UK, that means your account data
            stays within Europe in normal operation and no international transfer
            of it takes place.
          </P>
          <P>
            If you use Sign in with Apple, that exchange is governed by
            Apple&apos;s own privacy policy and infrastructure. Access by the
            developer for support or maintenance may take place from outside the
            EEA; where any transfer does occur, it relies on the European
            Commission&apos;s Standard Contractual Clauses.
          </P>
        </Sec>

        <Sec id="retention" n={8} title="How long it is kept">
          <P>
            Your account and reading progress are kept for as long as your
            account exists, because that is the point of them — reading progress
            you finished a year ago should still be there. There is no automatic
            expiry.
          </P>
          <P>
            When you delete your account, the account record and all associated
            reading progress are deleted immediately, as described below.
            Operational logs held by the hosting provider roll off on that
            provider&apos;s own short retention schedule.
          </P>
        </Sec>

        <Sec id="deletion" n={9} title="Deleting your account">
          <P>
            Deletion is built into the app: open <b>Settings</b>, then{' '}
            <b>Delete Account</b>. This removes your account and, in the same
            operation, every reading-progress record attached to it. The
            deletion is permanent and cannot be undone — the app warns you of
            this before you confirm.
          </P>
          <P>
            You do not need to email anyone or wait for a response, and there is
            no dark pattern in the way. If you would rather have the account
            deleted by request, write to <Mail /> from the address on the
            account and it will be done.
          </P>
          <P>
            Signing out, by contrast, leaves your account and progress intact so
            they are waiting for you when you sign back in.
          </P>
        </Sec>

        <Sec id="rights" n={10} title="Your rights">
          <P>
            Under the GDPR and UK GDPR you have the right to access your data, to
            correct it, to have it erased, to receive a copy in a portable
            format, to restrict or object to certain processing, and to withdraw
            consent where processing relies on it. Erasure is self-service in the
            app; for anything else, write to <Mail /> and expect a reply within
            30 days.
          </P>
          <P>
            If you are in California: HanDu does not sell or share personal
            information as those terms are defined by the CCPA/CPRA, and has not
            done so in the preceding twelve months. You have the right to know,
            to delete, and to correct, and you will never be discriminated
            against for exercising them.
          </P>
          <P>
            If you believe your data has been mishandled, you are entitled to
            complain to your local data protection authority. Raising it with the
            developer first is welcome but not a prerequisite.
          </P>
        </Sec>

        <Sec id="security" n={11} title="How it is protected">
          <UL
            items={[
              'All traffic between the app and the server is encrypted in transit with TLS.',
              'Passwords are stored only as salted hashes, handled by the authentication provider.',
              'Row Level Security is enforced on the database, so every query is checked server-side against the identity of the signed-in user. One account cannot read another account’s progress even if the app were modified to try.',
              'The account-deletion function identifies you from your session token rather than from a user ID sent by the app, so no client can ask it to delete somebody else.',
              'Session tokens are stored in the iOS Keychain, protected by the device’s own hardware-backed encryption.',
            ]}
          />
          <P>
            No system is perfectly secure, and no one can promise otherwise. If a
            breach affecting your personal data occurs, affected users and the
            relevant supervisory authority will be notified within the timeframes
            the law requires.
          </P>
        </Sec>

        <Sec id="children" n={12} title="Children's privacy">
          <P>
            HanDu is not directed at children under 13, and accounts are not
            knowingly created for them. If you believe a child has created an
            account, write to <Mail /> and it will be deleted promptly.
          </P>
        </Sec>

        <Sec id="labels" n={13} title="App Store privacy labels">
          <P>
            For reference, this is what the above corresponds to in Apple&apos;s
            App Store privacy questionnaire. None of it is used for tracking or
            advertising.
          </P>
          <Table
            head={['Category', 'Data type', 'Purpose', 'Linked to you']}
            rows={[
              ['Contact Info', 'Email address', 'App Functionality', 'Yes'],
              ['Identifiers', 'User ID', 'App Functionality', 'Yes'],
              ['Usage Data', 'None collected', '—', '—'],
              ['Diagnostics', 'None collected', '—', '—'],
            ]}
          />
        </Sec>

        <Sec id="changes" n={14} title="Changes to this policy">
          <P>
            If the app starts collecting something new, this page will say so and
            the date at the top will change. Material changes will also be
            surfaced in the app itself rather than quietly published here.
          </P>
        </Sec>

        <Sec id="contact" n={15} title="Contact">
          <P>
            Yusuf Brigui — <Mail />
          </P>
          <P className="text-sm text-handu-soft/80">
            {legal.copyright}. This policy applies to HanDu 汉读 for iOS.
          </P>
        </Sec>
      </article>
    </HanDuShell>
  )
}

/* ---------- small presentational helpers ---------- */

function Sec({ id, n, title, children }) {
  return (
    <motion.section
      id={id}
      variants={stagger}
      initial="hidden"
      whileInView="show"
      viewport={viewportOnce}
      className="mt-14 scroll-mt-24"
    >
      <motion.h2
        variants={fadeUp}
        className="border-b handu-gold-rule pb-3 text-2xl font-bold tracking-tight"
      >
        <span className="mr-2 text-handu-primary/50">{n}.</span>
        {title}
      </motion.h2>
      <motion.div variants={fadeUp} className="mt-5">
        {children}
      </motion.div>
    </motion.section>
  )
}

function H3({ children }) {
  return <h3 className="mb-2 mt-7 text-lg font-bold first:mt-0">{children}</h3>
}

function P({ children, className = '' }) {
  return (
    <p className={`mb-4 leading-relaxed text-handu-soft last:mb-0 ${className}`}>
      {children}
    </p>
  )
}

function UL({ items }) {
  return (
    <ul className="mb-4 space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex gap-3 leading-relaxed text-handu-soft">
          <span
            aria-hidden="true"
            className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-handu-gold"
          />
          {item}
        </li>
      ))}
    </ul>
  )
}

// Wide tables must scroll inside their own box rather than pushing the page
// sideways on a phone.
function Table({ head, rows }) {
  return (
    <div className="mb-4 overflow-x-auto rounded-xl border border-handu-outline/60">
      <table className="w-full min-w-[34rem] border-collapse text-left text-sm">
        <thead>
          <tr className="bg-handu-mutedbg/70">
            {head.map((h) => (
              <th key={h} className="px-4 py-3 font-semibold text-handu-ink">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]} className="border-t border-handu-outline/50">
              {row.map((cell, i) => (
                <td
                  key={i}
                  className={`px-4 py-3 align-top ${
                    i === 0 ? 'font-medium text-handu-ink' : 'text-handu-soft'
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Code({ children }) {
  return (
    <code className="rounded bg-handu-mutedbg px-1.5 py-0.5 text-[0.9em] text-handu-primary">
      {children}
    </code>
  )
}

function Mail() {
  return (
    <a
      href={`mailto:${legal.contactEmail}`}
      className="text-handu-primary underline decoration-handu-primary/30 underline-offset-2 transition-opacity hover:opacity-70"
    >
      {legal.contactEmail}
    </a>
  )
}
